import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Button, Input, PasswordInput, Select, Badge, Modal, useToast } from '../../../shared/components';
import { getTurnoGeoRepository } from '../../../shared/services/turnoGeo/repositoryFactory';
import { Employee, EmployeePatch, HomeLocation, LocationKind } from '../../../shared/services/turnoGeo/types';
import { LeafletMap, MapPlace } from './LeafletMap';

const repo = getTurnoGeoRepository();

const KIND_TITLE: Record<LocationKind, string> = { OFICINA: 'Oficina', CASA: 'Remoto' };
const GENDER_OPTIONS = [{ value: 'F', label: 'Femenino' }, { value: 'M', label: 'Masculino' }];

/** Acepta "6.2442, -75.5812" (como lo copia Google Maps) y devuelve [lat, lon]. */
function parseLatLon(text: string): [number, number] | null {
  const m = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lon = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lat, lon] : null;
}

/** Bloque de coordenadas de un tipo (Oficina / Remoto): lat, lon, radio, pegar "lat, lon", mapa con clic. */
function LocationEditor({ userId, kind, current, onChanged }: { userId: string; kind: LocationKind; current: HomeLocation | undefined; onChanged: () => void }) {
  const toast = useToast();
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [radius, setRadius] = useState('100');
  const [paste, setPaste] = useState('');
  const [showMap, setShowMap] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLat(current?.gpsLatitude != null ? String(current.gpsLatitude) : '');
    setLon(current?.gpsLongitude != null ? String(current.gpsLongitude) : '');
    setRadius(String(current?.gpsRadiusMeters ?? 100));
  }, [current?.id, current?.gpsLatitude, current?.gpsLongitude, current?.gpsRadiusMeters]);

  const latN = Number(lat);
  const lonN = Number(lon);
  const valid = lat !== '' && lon !== '' && Number.isFinite(latN) && Number.isFinite(lonN) && Math.abs(latN) <= 90 && Math.abs(lonN) <= 180;
  const radiusN = Number(radius) || 100;
  const places: MapPlace[] = useMemo(() => (valid ? [{ kind: kind === 'OFICINA' ? 'oficina' : 'casa', name: KIND_TITLE[kind], lat: latN, lon: lonN, radius: radiusN, status: current?.status ?? 'APROBADA' }] : []), [valid, kind, latN, lonN, radiusN, current?.status]);

  const applyPaste = (text: string) => {
    setPaste(text);
    const p = parseLatLon(text);
    if (p) { setLat(String(p[0])); setLon(String(p[1])); }
  };
  const save = async () => {
    if (!valid) { toast.show('Escribe una latitud y longitud válidas.', 'error'); return; }
    setSaving(true);
    try {
      await repo.adminSetLocation({ userId, locationKind: kind, gpsLatitude: latN, gpsLongitude: lonN, gpsRadiusMeters: radiusN });
      toast.show(`${KIND_TITLE[kind]} guardada.`);
      onChanged();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar.', 'error'); }
    finally { setSaving(false); }
  };
  const review = async (status: 'APROBADA' | 'RECHAZADA') => {
    if (!current) return;
    try { await repo.reviewHomeLocation(current.id, status); toast.show(status === 'APROBADA' ? 'Ubicación aprobada.' : 'Ubicación rechazada.'); onChanged(); }
    catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo.', 'error'); }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-ink-100 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-ink-800">{KIND_TITLE[kind]}</p>
        {current ? <Badge tone={current.status === 'APROBADA' ? 'success' : current.status === 'PENDIENTE' ? 'warning' : 'critical'}>{current.status.toLowerCase()}</Badge> : <Badge tone="neutral">sin registrar</Badge>}
      </div>
      <Input label="Pegar coordenadas (lat, lon)" placeholder="6.2442, -75.5812" value={paste} onChange={(e) => applyPaste(e.target.value)} />
      <div className="grid grid-cols-3 gap-2">
        <Input label="Latitud" inputMode="decimal" value={lat} onChange={(e) => setLat(e.target.value)} />
        <Input label="Longitud" inputMode="decimal" value={lon} onChange={(e) => setLon(e.target.value)} />
        <Input label="Radio (m)" inputMode="numeric" value={radius} onChange={(e) => setRadius(e.target.value)} />
      </div>
      {showMap && (
        <>
          <LeafletMap places={places} pick={valid ? { lat: latN, lon: lonN } : null} onPick={(a, b) => { setLat(a.toFixed(6)); setLon(b.toFixed(6)); }} className="h-64" fitKey={`${kind}-${valid ? 'v' : 'n'}`} />
          <p className="text-xs text-ink-400">Toca el mapa para fijar el punto.</p>
        </>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={() => setShowMap((v) => !v)}>{showMap ? 'Ocultar mapa' : '📍 Ver / elegir en el mapa'}</Button>
        <Button onClick={save} isLoading={saving} disabled={!valid}>Guardar {KIND_TITLE[kind].toLowerCase()}</Button>
        {current?.status === 'PENDIENTE' && (
          <>
            <Button variant="outline" onClick={() => review('APROBADA')}>Aprobar</Button>
            <Button variant="danger" onClick={() => review('RECHAZADA')}>Rechazar</Button>
          </>
        )}
      </div>
      {current?.publicIpAddress && <p className="break-all text-xs text-ink-400">IP registrada: {current.publicIpAddress} (solo respaldo si el GPS no sirve)</p>}
    </div>
  );
}

/**
 * Ficha editable del empleado (Acceso y usuarios > Usuarios): datos del Excel (cédula, celular, género, proceso,
 * centro de costos, ciudad, contratación, estado, ítem, IP y salario) + coordenadas de Oficina y Remoto.
 */
export function EmployeeFichaModal({ open, onClose, userId, fullName, email, employee, header, extra, onSaved }: { open: boolean; onClose: () => void; userId: string; fullName: string; email: string; employee: Employee | undefined; header?: ReactNode; extra?: ReactNode; onSaved: () => void }) {
  const toast = useToast();
  const [f, setF] = useState({ documentNumber: '', phone: '', gender: '', process: '', costCenter: '', city: '', contractType: '', employeeStatus: 'ACTIVO', item: '', expectedIp: '', salary: '' });
  const [saving, setSaving] = useState(false);
  const [locs, setLocs] = useState<HomeLocation[]>([]);

  useEffect(() => {
    if (!open) return;
    const e = employee;
    setF({
      documentNumber: e?.documentNumber ?? '', phone: e?.phone ?? '', gender: e?.gender ?? '', process: e?.process ?? '', costCenter: e?.costCenter ?? '',
      city: e?.city ?? '', contractType: e?.contractType ?? '', employeeStatus: e?.employeeStatus ?? 'ACTIVO', item: e?.itemNo != null ? String(e.itemNo) : '',
      expectedIp: e?.expectedIp ?? '', salary: e?.salary != null ? String(e.salary) : '',
    });
  }, [open, employee]);

  const loadLocs = () => repo.listHomeLocations().then((all) => setLocs(all.filter((l) => l.userId === userId))).catch(() => setLocs([]));
  useEffect(() => { if (open) void loadLocs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open, userId]);

  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      const nz = (v: string) => (v.trim() === '' ? null : v.trim());
      const patch: EmployeePatch = {
        documentNumber: nz(f.documentNumber), phone: nz(f.phone), gender: nz(f.gender), process: nz(f.process), costCenter: nz(f.costCenter),
        city: nz(f.city), contractType: nz(f.contractType), employeeStatus: f.employeeStatus.trim().toUpperCase() || 'ACTIVO',
        item: nz(f.item), expectedIp: nz(f.expectedIp), salary: nz(f.salary),
      };
      await repo.updateEmployee(userId, patch);
      toast.show('Ficha guardada.');
      onSaved();
    } catch (e) { toast.show(e instanceof Error ? e.message : 'No se pudo guardar la ficha.', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={fullName || 'Ficha'}>
      <div className="space-y-4">
        {header}
        <p className="break-all text-xs text-ink-500">Correo (usuario): <strong className="text-ink-800">{email}</strong></p>
        {!employee && <p className="rounded-xl bg-ink-50 p-3 text-xs text-ink-500">Esta persona no tiene ficha laboral cargada. Si llenas los datos y guardas, se crea.</p>}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Cédula" value={f.documentNumber} onChange={set('documentNumber')} />
          <Input label="Celular" inputMode="tel" value={f.phone} onChange={set('phone')} />
          <Select label="Género" value={f.gender} onChange={set('gender')} options={GENDER_OPTIONS} placeholder="Sin definir" />
          <Input label="Proceso" value={f.process} onChange={set('process')} />
          <Input label="Centro de costos" value={f.costCenter} onChange={set('costCenter')} />
          <Input label="Ciudad" value={f.city} onChange={set('city')} />
          <Input label="Contratación" value={f.contractType} onChange={set('contractType')} />
          <Input label="Estado" value={f.employeeStatus} onChange={set('employeeStatus')} />
          <Input label="Ítem" inputMode="numeric" value={f.item} onChange={set('item')} />
          <Input label="IP esperada" value={f.expectedIp} onChange={set('expectedIp')} />
          <PasswordInput label="Salario" value={f.salary} onChange={set('salary')} />
        </div>
        {extra}
        <Button fullWidth onClick={save} isLoading={saving}>Guardar ficha</Button>

        <div className="space-y-3">
          <p className="text-sm font-semibold text-ink-800">Ubicaciones de referencia (GPS)</p>
          <LocationEditor userId={userId} kind="OFICINA" current={locs.find((l) => l.locationKind === 'OFICINA')} onChanged={loadLocs} />
          <LocationEditor userId={userId} kind="CASA" current={locs.find((l) => l.locationKind === 'CASA')} onChanged={loadLocs} />
        </div>
        <Button variant="outline" fullWidth onClick={onClose}>Cerrar</Button>
      </div>
    </Modal>
  );
}
